export type NewsListSkeletonVariant = 'news' | 'events' | 'programs';

const DEFAULT_SKELETON_COUNT: Record<NewsListSkeletonVariant, number> = {
  news: 6,
  events: 3,
  programs: 3,
};

function createEl<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  attrs?: Record<string, string>,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (attrs) {
    Object.entries(attrs).forEach(([key, value]) => {
      el.setAttribute(key, value);
    });
  }
  return el;
}

export function createNewsCardSkeleton(): HTMLElement {
  const article = createEl('article', 'news-card-skeleton', {
    'aria-hidden': 'true',
    'data-news-skeleton': '',
  });

  article.append(
    createEl('div', 'news-card-skeleton__media'),
    createEl('div', 'news-card-skeleton__date'),
    createEl('div', 'news-card-skeleton__title'),
    createEl('div', 'news-card-skeleton__line'),
    createEl('div', 'news-card-skeleton__line'),
    createEl('div', 'news-card-skeleton__line news-card-skeleton__line--short'),
  );

  return article;
}

export function createEventsCalendarItemSkeleton(): HTMLElement {
  const template = document.createElement('template');
  template.innerHTML = `
    <div class="events-calendar-item" data-events-skeleton aria-hidden="true">
      <div class="events-calendar-item-date events-calendar-item-date--skeleton">
        <span class="event-card-skeleton__bar event-card-skeleton__bar--day"></span>
        <div class="month-container">
          <span class="event-card-skeleton__bar event-card-skeleton__bar--month"></span>
          <span class="event-card-skeleton__bar event-card-skeleton__bar--time"></span>
        </div>
      </div>
      <article class="event-card event-card--skeleton">
        <div class="event-card-image-container">
          <div class="event-card-skeleton__media"></div>
        </div>
        <div class="event-card-body">
          <div class="event-card-header">
            <div class="event-card-meta">
              <div class="event-card-date event-card-date--list">
                <span class="event-card-skeleton__bar event-card-skeleton__bar--day"></span>
                <div class="event-card-date-text">
                  <span class="event-card-skeleton__bar event-card-skeleton__bar--month"></span>
                  <span class="event-card-skeleton__bar event-card-skeleton__bar--time"></span>
                </div>
              </div>
              <span class="event-card-skeleton__bar event-card-skeleton__bar--divider-vertical" aria-hidden="true"></span>
              <div class="event-card-location">
                <span class="event-card-skeleton__bar event-card-skeleton__bar--location"></span>
                <span class="event-card-skeleton__bar event-card-skeleton__bar--room"></span>
              </div>
            </div>
            <div class="event-card-status">
              <span class="event-card-skeleton__bar event-card-skeleton__bar--status"></span>
            </div>
          </div>
          <div class="event-card-format">
            <span class="event-card-skeleton__bar event-card-skeleton__bar--dot"></span>
            <span class="event-card-skeleton__bar event-card-skeleton__bar--format"></span>
          </div>
          <div class="event-card-skeleton__bar event-card-skeleton__bar--title"></div>
          <div class="event-card-skeleton__bar"></div>
          <div class="event-card-skeleton__bar"></div>
          <div class="event-card-skeleton__bar event-card-skeleton__bar--short"></div>
        </div>
      </article>
    </div>
  `.trim();

  return template.content.firstElementChild as HTMLElement;
}

export function createEducationProgramCardSkeleton(): HTMLElement {
  const template = document.createElement('template');
  template.innerHTML = `
    <article class="education-program-card education-program-card--skeleton" aria-hidden="true" data-education-program-skeleton>
      <div class="education-program-card__link">
        <div class="education-program-card-skeleton__media"></div>
        <div class="education-program-card__body">
          <div class="education-program-card-skeleton__head">
            <div class="education-program-card-skeleton__dates">
              <span class="education-program-card-skeleton__bar education-program-card-skeleton__bar--day"></span>
              <span class="education-program-card-skeleton__bar education-program-card-skeleton__bar--date"></span>
            </div>
            <span class="education-program-card-skeleton__bar education-program-card-skeleton__bar--status"></span>
          </div>
          <span class="education-program-card-skeleton__bar education-program-card-skeleton__bar--title"></span>
          <span class="education-program-card-skeleton__bar"></span>
          <span class="education-program-card-skeleton__bar"></span>
          <span class="education-program-card-skeleton__bar education-program-card-skeleton__bar--short"></span>
        </div>
      </div>
    </article>
  `.trim();

  return template.content.firstElementChild as HTMLElement;
}

export function fillGridWithSkeletons(
  grid: HTMLElement,
  variant: NewsListSkeletonVariant,
  count = DEFAULT_SKELETON_COUNT[variant],
): void {
  const factory =
    variant === 'news'
      ? createNewsCardSkeleton
      : variant === 'programs'
        ? createEducationProgramCardSkeleton
        : createEventsCalendarItemSkeleton;

  grid.replaceChildren();

  for (let i = 0; i < count; i += 1) {
    grid.append(factory());
  }
}

export function cloneGridContent(grid: HTMLElement): DocumentFragment {
  const snapshot = document.createDocumentFragment();
  Array.from(grid.children).forEach((child) => {
    snapshot.append(child.cloneNode(true));
  });
  return snapshot;
}

export function cloneFragment(fragment: DocumentFragment): DocumentFragment {
  const copy = document.createDocumentFragment();
  fragment.childNodes.forEach((node) => {
    copy.append(node.cloneNode(true));
  });
  return copy;
}

export function restoreGridContent(grid: HTMLElement, snapshot: DocumentFragment): void {
  grid.replaceChildren();
  grid.append(snapshot);
}
